import { supabase } from "@/integrations/supabase/client";

const REF_KEY = "td_referral_code";

/** Reads ?ref=CODE from the URL and remembers it until the visitor purchases. */
export function captureReferralFromUrl(search: string) {
  try {
    const code = new URLSearchParams(search).get("ref");
    if (!code) return;
    const clean = code.trim().toUpperCase();
    if (!/^[A-Z0-9]{6,12}$/.test(clean)) return;
    if (!localStorage.getItem(REF_KEY)) localStorage.setItem(REF_KEY, clean);
  } catch {
    /* storage unavailable — referral simply isn't attributed */
  }
}

export function storedReferralCode(): string | null {
  try {
    return localStorage.getItem(REF_KEY);
  } catch {
    return null;
  }
}

export function clearStoredReferralCode() {
  try {
    localStorage.removeItem(REF_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Attaches a stored referral code to the signed-in user. Safe to call repeatedly:
 * the backend rejects self-referrals, duplicates and already-licensed accounts.
 */
export async function claimStoredReferral(): Promise<void> {
  const code = storedReferralCode();
  if (!code) return;
  const { data, error } = await supabase.functions.invoke("referral-claim", {
    body: { code, device: deviceFingerprint() },
  });
  if (error) return; // keep the code stored and retry on the next session
  const result = data as { claimed?: boolean; reason?: string } | null;
  if (
    result?.claimed ||
    result?.reason === "already_attributed" ||
    result?.reason === "self_referral" ||
    result?.reason === "blocked_fraud_check"
  ) {
    clearStoredReferralCode();
  }
}

/**
 * Coarse, non-unique device signature used only for referral fraud scoring.
 * Deliberately excludes anything that could identify a person; the backend
 * stores it salted and hashed.
 */
export function deviceFingerprint(): string {
  try {
    const n = navigator as Navigator & { hardwareConcurrency?: number; deviceMemory?: number };
    return [
      screen.width,
      screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      n.language,
      n.hardwareConcurrency ?? 0,
      n.deviceMemory ?? 0,
      n.platform ?? "",
    ].join("|");
  } catch {
    return "unknown";
  }
}

export function referralLink(code: string): string {
  return `${window.location.origin}/?ref=${code}`;
}
